import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@/context/ThemeContextOptimized';
import { getFirebaseAuth } from '@/services/firebase';
import LogoutConfirmModal from '@/components/LogoutConfirmModal';

export interface ShellUser {
  displayName?: string;
  email?: string;
  photoURL?: string;
  [key: string]: unknown;
}

export interface AccountMenuProps {
  user: ShellUser | null;
  role: string;
  avatarURL: string;
  /** Where the menu opens from: below (top bar) or to the right (side rail). */
  placement?: 'below' | 'right';
}

const ROLE_LABEL = new Map<string, string>([
  ['admin', 'Admin'], ['doctor', 'Physician'], ['provider', 'Provider'], ['physician', 'Physician'],
  ['nurse', 'Nurse'], ['front_desk', 'Front desk'], ['billing', 'Billing'], ['staff', 'Staff'],
]);

/**
 * Avatar button + account menu. Sign out is an explicit item (it used to be a
 * hidden one-second long-press on the avatar).
 */
export default function AccountMenu({ user, role, avatarURL, placement = 'below' }: AccountMenuProps) {
  const navigate = useNavigate();
  const { darkMode, toggleDarkMode } = useTheme() as { darkMode: boolean; toggleDarkMode: () => Promise<boolean> };
  const [open, setOpen] = useState(false);
  const [confirmSignOut, setConfirmSignOut] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener('pointerdown', onDown, true);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const go = useCallback((path: string) => {
    setOpen(false);
    navigate(path);
  }, [navigate]);

  const signOut = useCallback(async () => {
    const auth = await getFirebaseAuth();
    const { signOut: fbSignOut } = await import('firebase/auth');
    await fbSignOut(auth);
    setConfirmSignOut(false);
    navigate('/login');
  }, [navigate]);

  const name = user?.displayName || user?.email || 'Account';
  const initials = String(name).split(/\s+/).map((w) => w[0] ?? '').join('').slice(0, 2).toUpperCase();
  const item = 'flex w-full items-center justify-between rounded-md px-2.5 py-2 text-left text-sm text-slate-800 hover:bg-slate-100 dark:text-slate-100 dark:hover:bg-slate-800';

  return (
    <div ref={rootRef} className="relative flex-none">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Account menu, ${name}`}
        className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-full bg-primary-50 text-xs font-semibold text-primary-700 ring-1 ring-slate-200 hover:ring-primary-400 dark:bg-primary-900/40 dark:text-primary-200 dark:ring-slate-700"
      >
        {avatarURL ? (
          <img
            src={avatarURL}
            alt=""
            className="h-full w-full object-cover"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        ) : initials}
      </button>
      {open && (
        <div
          role="menu"
          aria-label="Account"
          className={`absolute z-[60] w-60 rounded-xl border border-slate-200 bg-white p-1.5 shadow-lg dark:border-slate-700 dark:bg-slate-900 ${placement === 'below' ? 'right-0 top-10' : 'bottom-0 left-11'}`}
        >
          <div className="mb-1 border-b border-slate-100 px-2.5 pb-2.5 pt-1.5 dark:border-slate-800">
            <div className="truncate text-sm font-semibold text-slate-900 dark:text-slate-50">{name}</div>
            <div className="text-xs text-slate-600 dark:text-slate-300">{ROLE_LABEL.get(role) ?? role}</div>
          </div>
          <button type="button" role="menuitem" className={item} onClick={() => go('/account')}>Account &amp; settings</button>
          <button type="button" role="menuitemcheckbox" aria-checked={darkMode} className={item} onClick={() => { void toggleDarkMode(); }}>
            Dark mode <span className="text-xs text-slate-600 dark:text-slate-300">{darkMode ? 'On' : 'Off'}</span>
          </button>
          {role === 'admin' && <button type="button" role="menuitem" className={item} onClick={() => go('/admin')}>Admin</button>}
          <button
            type="button"
            role="menuitem"
            className={`${item} mt-1 border-t border-slate-100 text-red-700 dark:border-slate-800 dark:text-red-300`}
            onClick={() => { setOpen(false); setConfirmSignOut(true); }}
          >
            Sign out
          </button>
        </div>
      )}
      <LogoutConfirmModal isOpen={confirmSignOut} onClose={() => setConfirmSignOut(false)} onConfirm={signOut} user={user} />
    </div>
  );
}
